import { Check, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

interface PasswordStrengthIndicatorProps {
  password: string;
}

const CRITERIA = [
  { key: "length", label: "8 caractères", test: (p: string) => p.length >= 8 },
  { key: "upper", label: "1 majuscule", test: (p: string) => /[A-Z]/.test(p) },
  { key: "digit", label: "1 chiffre", test: (p: string) => /[0-9]/.test(p) },
];

const LEVEL_LABELS = ["Trop court", "Faible", "Moyen", "Bon", "Excellent"];
const LEVEL_COLORS = ["bg-muted", "bg-destructive", "bg-warning", "bg-primary", "bg-success"];

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const checks = CRITERIA.map((c) => ({ ...c, valid: c.test(password) }));
  const score = checks.filter((c) => c.valid).length;
  // 4 segments : 0 = vide, 1 = faible, 2 = moyen, 3 = bon, 4 = excellent (3 critères + bonus longueur ≥12).
  const bonus = password.length >= 12 ? 1 : 0;
  const level = password.length === 0 ? 0 : Math.min(4, score + bonus);

  return (
    <div className="space-y-2 mt-2" role="status" aria-live="polite">
      <div className="flex items-center gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            aria-hidden="true"
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors duration-base ease-motion-out",
              i < level ? LEVEL_COLORS[level] : "bg-muted",
            )}
          />
        ))}
        <span
          className={cn(
            "ml-2 text-xs font-medium tabular-nums min-w-[64px] text-right transition-colors duration-base",
            level === 0 && "text-muted-foreground",
            level === 1 && "text-destructive",
            level === 2 && "text-warning",
            level === 3 && "text-primary",
            level === 4 && "text-success",
          )}
        >
          {password.length === 0 ? "" : LEVEL_LABELS[level]}
        </span>
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1">
        {checks.map((c) => (
          <li
            key={c.key}
            className={cn(
              "inline-flex items-center gap-1 text-xs transition-colors duration-fast",
              c.valid ? "text-success" : "text-muted-foreground",
            )}
          >
            {c.valid ? (
              <Check className="w-3 h-3" strokeWidth={3} />
            ) : (
              <Circle className="w-3 h-3" strokeWidth={2} />
            )}
            <span>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
